"use client";

import { useState, useEffect, Suspense } from "react";
import { base58, base64 } from "@scure/base";
import { md5 } from "js-md5";
import { useSearchParams } from "next/navigation";

interface ApiResponse {
  signedUrl?: string;
  headers?: Record<string, string>;
  [key: string]: unknown;
}

// File System Access API types
interface FileSystemHandle {
  createWritable(): Promise<FileSystemWritableFileStream>;
}

interface FileSystemWritableFileStream extends WritableStream {
  write(data: FileSystemWriteChunkType): Promise<void>;
  seek(position: number): Promise<void>;
  truncate(size: number): Promise<void>;
}

type FileSystemWriteChunkType = BufferSource | Blob | string;

declare global {
  interface Window {
    showSaveFilePicker(options?: {
      suggestedName?: string;
      types?: Array<{
        description: string;
        accept: Record<string, string[]>;
      }>;
    }): Promise<FileSystemHandle>;
  }
}

// Function to convert base58 to base64
function base58ToBase64(base58String: string): string {
  try {
    const decoded = base58.decode(base58String);
    return base64.encode(decoded);
  } catch {
    throw new Error("Invalid base58 string");
  }
}

// Helper function to convert hex string to Uint8Array (browser-compatible)
function hexToUint8Array(hex: string): Uint8Array {
  const bytes = new Uint8Array(hex.length / 2);
  for (let i = 0; i < hex.length; i += 2) {
    bytes[i / 2] = parseInt(hex.slice(i, i + 2), 16);
  }
  return bytes;
}

// Helper function to convert base64 string to Uint8Array (browser-compatible)
function base64ToUint8Array(base64Str: string): Uint8Array {
  const binaryString = atob(base64Str);
  const bytes = new Uint8Array(binaryString.length);
  for (let i = 0; i < binaryString.length; i++) {
    bytes[i] = binaryString.charCodeAt(i);
  }
  return bytes;
}

// Function to calculate MD5 hash and return as base64
async function calculateMD5Base64(data: Uint8Array): Promise<string> {
  // Use js-md5 package to calculate MD5 hash
  const hash = md5.hex(data);
  // Convert hex string to base64
  return base64.encode(hexToUint8Array(hash));
}

// Separate component that uses useSearchParams
function ShareForm() {
  const [base58Key, setBase58Key] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [downloadUrl, setDownloadUrl] = useState<string | null>(null);
  const [downloadProgress, setDownloadProgress] = useState<number>(0);
  const [isDownloading, setIsDownloading] = useState(false);
  const [showDownloadNotification, setShowDownloadNotification] =
    useState(false);
  const [ipAddress, setIpAddress] = useState<string | null>(null);
  const [location, setLocation] = useState<string | null>(null);
  const [loadingLocation, setLoadingLocation] = useState(true);

  const searchParams = useSearchParams();
  const p = searchParams.get("p");
  const s = searchParams.get("s");
  const i = searchParams.get("i");

  // Function to fetch IP address and location
  const fetchIpAndLocation = async () => {
    try {
      setLoadingLocation(true);

      // Fetch IP address
      const ipResponse = await fetch("https://api.ipify.org?format=json");
      const ipData = (await ipResponse.json()) as { ip: string };
      setIpAddress(ipData.ip);

      // Fetch location data using IP
      const locationResponse = await fetch(
        `https://ipapi.co/${ipData.ip}/json/`
      );
      const locationData = (await locationResponse.json()) as {
        error?: boolean;
        city?: string;
        region?: string;
        country_name?: string;
      };

      if (locationData.error !== true) {
        const locationString = [
          locationData.city,
          locationData.region,
          locationData.country_name,
        ]
          .filter(Boolean)
          .join(", ");
        setLocation(locationString);
      } else {
        setLocation("Location unavailable");
      }
    } catch (error) {
      console.error("Error fetching IP/location:", error);
      setIpAddress("IP address unavailable");
      setLocation("Location unavailable");
    } finally {
      setLoadingLocation(false);
    }
  };

  // Fetch IP and location on component mount
  useEffect(() => {
    fetchIpAndLocation();
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError(null);
    setDownloadUrl(null);
    setDownloadProgress(0);

    try {
      // Validate base58 key first
      if (!base58Key.trim()) {
        throw new Error("Decryption key is required");
      }

      // Try to decode the base58 key to validate it
      try {
        base58ToBase64(base58Key);
      } catch {
        throw new Error("Invalid decryption key format");
      }

      // Get URL parameters
      const p = searchParams.get("p");
      const s = searchParams.get("s");
      const i = searchParams.get("i");

      // Make request to our API route
      const response = await fetch("/api/share", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          privateAccessKey: p,
          keyBase64: s,
          item: i,
          ip: ipAddress,
          geolocation: location ? { location } : undefined,
          filename: `Archive.zip`,
        }),
      });

      if (!response.ok) {
        const errorData: { error?: string } = await response.json();
        throw new Error(
          errorData.error ||
            `API request failed: ${response.status} ${response.statusText}`
        );
      }

      const data: ApiResponse = await response.json();

      // Store download information if we have a signed URL
      if (data.signedUrl) {
        setDownloadUrl(data.signedUrl);
      }
    } catch (err) {
      setError(
        err instanceof Error ? err.message : "An unknown error occurred"
      );
    } finally {
      setLoading(false);
    }
  };

  const handleDownload = async () => {
    if (!downloadUrl) return;

    try {
      setIsDownloading(true);
      setDownloadProgress(0);
      setError(null);

      const base64Key = base58ToBase64(base58Key);
      const headers = {
        "x-amz-server-side-encryption-customer-algorithm": "AES256",
        "x-amz-server-side-encryption-customer-key": base64Key,
        "x-amz-server-side-encryption-customer-key-md5":
          await calculateMD5Base64(
            base64ToUint8Array(base64Key)
          ),
      };

      // Fetch the file with encryption headers
      const response = await fetch(downloadUrl, {
        method: "GET",
        headers,
      });

      if (!response.ok) {
        throw new Error(
          `Download failed: ${response.status} ${response.statusText}`
        );
      }

      // Get content length for progress tracking
      const contentLength = response.headers.get("content-length");
      const total = contentLength ? parseInt(contentLength, 10) : 0;
      let downloaded = 0;

      // Try to use File System Access API first (modern browsers)
      if ("showSaveFilePicker" in window) {
        try {
          const filename = `Sprigings-Share-${new Date()
            .toLocaleString()
            .replace(/[^a-zA-Z0-9]/g, "")}.zip`;
          const fileHandle = await window.showSaveFilePicker({
            suggestedName: filename,
            types: [
              {
                description: "ZIP Archive",
                accept: {
                  "application/zip": [".zip"],
                },
              },
            ],
          });

          const writable = await fileHandle.createWritable();
          const reader = response.body?.getReader();

          if (!reader) {
            throw new Error("Failed to create download stream");
          }

          while (true) {
            const { done, value } = await reader.read();

            if (done) break;

            await writable.write(value);
            downloaded += value.length;

            // Update progress
            if (total > 0) {
              const progress = Math.round((downloaded / total) * 100);
              setDownloadProgress(progress);
            } else {
              setDownloadProgress(Math.min(95, downloaded / 1024 / 1024));
            }
          }

          await writable.close();
          setDownloadProgress(100);
          setShowDownloadNotification(true);
          return;
        } catch (fsError) {
          console.log(
            "File System Access API failed, falling back to blob download:",
            fsError
          );
          // Continue to blob fallback
        }
      }

      // Fallback to blob-based download
      const reader = response.body?.getReader();
      if (!reader) {
        throw new Error("Failed to create download stream");
      }

      const chunks: Uint8Array[] = [];

      while (true) {
        const { done, value } = await reader.read();

        if (done) break;

        chunks.push(value);
        downloaded += value.length;

        // Update progress
        if (total > 0) {
          const progress = Math.round((downloaded / total) * 100);
          setDownloadProgress(progress);
        } else {
          setDownloadProgress(Math.min(95, downloaded / 1024 / 1024));
        }
      }

      // Combine chunks into a single blob
      const blob = new Blob(chunks as BlobPart[]);
      const blobUrl = window.URL.createObjectURL(blob);

      // Create download link
      const a = document.createElement("a");
      a.download = `Sprigings-Share-${new Date()
        .toLocaleString()
        .replace(/[^a-zA-Z0-9]/g, "")}.zip`;
      a.href = blobUrl;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);

      // Clean up blob URL
      setTimeout(() => {
        window.URL.revokeObjectURL(blobUrl);
      }, 1000);

      setDownloadProgress(100);
      setShowDownloadNotification(true);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Download failed");
    } finally {
      setIsDownloading(false);
    }
  };

  // Return blank page if required parameters are missing
  if (!p || !s || !i) {
    return (
      <div className="flex items-center justify-center h-screen">
        <svg
          viewBox="0 0 94 149"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
          className="w-6 h-6"
        >
          <path
            d="M91.9434 54.5842C93.0429 51.0467 93.1261 48.9871 93.1261 25.3892V0L84.2928 0.0197565C77.5052 0.0347283 74.7642 0.27968 72.4573 1.07738C67.9754 2.62666 64.3635 4.94248 61.2709 8.24886C55.8122 14.0843 54.1995 19.2957 54.1995 31.0958V39.183L53.9 72.763L64.0808 72.4623C73.6305 72.1803 74.482 72.0581 77.8129 70.4998C84.8898 67.1881 89.7156 61.7527 91.9434 54.5842Z"
            fill="#006999"
          ></path>
          <path
            d="M39.5011 86.9256L39.8185 74.0498L40.1179 40.4698L22.238 58.4576C3.11958 77.6921 1.9416 79.2115 0.577966 86.4009C0.246191 88.1503 -0.00355495 102.341 3.82848e-05 119.196L0.00663597 148.909L15.7234 133.039C32.6326 115.965 34.1543 113.992 37.3248 105.048C39.1274 99.9612 39.1938 99.4091 39.5011 86.9256Z"
            fill="#ECBB44"
          ></path>
        </svg>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header with Evatir branding */}
      <header className="py-4">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-2">
              <svg
                viewBox="0 0 94 149"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
                className="w-6 h-6"
              >
                <path
                  d="M91.9434 54.5842C93.0429 51.0467 93.1261 48.9871 93.1261 25.3892V0L84.2928 0.0197565C77.5052 0.0347283 74.7642 0.27968 72.4573 1.07738C67.9754 2.62666 64.3635 4.94248 61.2709 8.24886C55.8122 14.0843 54.1995 19.2957 54.1995 31.0958V39.183L53.9 72.763L64.0808 72.4623C73.6305 72.1803 74.482 72.0581 77.8129 70.4998C84.8898 67.1881 89.7156 61.7527 91.9434 54.5842Z"
                  fill="#006999"
                ></path>
                <path
                  d="M39.5011 86.9256L39.8185 74.0498L40.1179 40.4698L22.238 58.4576C3.11958 77.6921 1.9416 79.2115 0.577966 86.4009C0.246191 88.1503 -0.00355495 102.341 3.82848e-05 119.196L0.00663597 148.909L15.7234 133.039C32.6326 115.965 34.1543 113.992 37.3248 105.048C39.1274 99.9612 39.1938 99.4091 39.5011 86.9256Z"
                  fill="#ECBB44"
                ></path>
              </svg>
              <div>
                <h1 className="text-sm font-medium text-gray-700">Share</h1>
                <p className="text-xs text-gray-500">
                  End-to-end encrypted file sharing
                </p>
              </div>
            </div>
          </div>
        </div>
      </header>

      <div className="py-12 px-4 sm:px-6 lg:px-8">
        <div className="max-w-md mx-auto">
          <div className="bg-white shadow-lg rounded-lg p-6">
            <div className="text-center mb-6">
              <h2 className="text-2xl font-bold text-gray-900 mb-2">
                Secure File Retrieval
              </h2>
              <p className="text-sm text-gray-600">
                Submit the provided decryption key to access the shared files
              </p>
            </div>

            {loadingLocation ? (
              <div className="mb-4 p-3 bg-blue-50 border border-blue-200 rounded-md">
                <p className="text-center text-blue-600">
                  Retrieving network information...
                </p>
              </div>
            ) : (
              <div className="mb-4 p-3 bg-yellow-50 border border-yellow-200 rounded-md">
                <h3 className="text-sm font-medium text-yellow-800 mb-2">
                  Information that will be recorded:
                </h3>
                <div className="text-sm text-yellow-700">
                  <p>
                    <strong>IP Address:</strong> {ipAddress}
                  </p>
                  <p>
                    <strong>Geographic Location:</strong> {location}
                  </p>
                  <p className="text-xs mt-2 text-yellow-600">
                    ⚠️ This information will be logged upon form submission
                  </p>
                </div>
              </div>
            )}

            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label
                  htmlFor="base58Key"
                  className="block text-sm font-medium text-gray-700 mb-2"
                >
                  Decryption Key
                </label>
                <input
                  id="base58Key"
                  type="text"
                  value={base58Key}
                  onChange={(e) => setBase58Key(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  placeholder="Enter decryption key..."
                  required
                />
              </div>

              <button
                type="submit"
                disabled={loading}
                className="w-full bg-blue-600 text-white py-2 px-4 rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {loading ? "Processing..." : "Verify Access"}
              </button>
            </form>

            {error && (
              <div className="mt-4 p-4 bg-red-50 border border-red-200 rounded-md">
                <p className="text-red-800 text-sm">{error}</p>
              </div>
            )}

            {downloadUrl && (
              <div className="mt-4">
                <button
                  onClick={handleDownload}
                  disabled={isDownloading}
                  className="w-full bg-green-600 text-white py-2 px-4 rounded-md hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-green-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isDownloading ? "Downloading..." : "Download Files"}
                </button>

                {isDownloading && (
                  <div className="mt-3">
                    <div className="flex justify-between text-sm text-gray-600 mb-1">
                      <span>Downloading...</span>
                      <span>{downloadProgress}%</span>
                    </div>
                    <div className="w-full bg-gray-200 rounded-full h-2">
                      <div
                        className="bg-green-600 h-2 rounded-full transition-all duration-300 ease-out"
                        style={{ width: `${downloadProgress}%` }}
                      ></div>
                    </div>
                    <p className="text-xs text-gray-500 mt-1">
                      {downloadProgress < 100
                        ? "Please wait, this may take a while for large files..."
                        : "Download complete!"}
                    </p>
                  </div>
                )}
              </div>
            )}

            {showDownloadNotification && (
              <div className="mt-4 p-4 bg-green-50 border border-green-200 rounded-md">
                <div className="flex items-start">
                  <div className="flex-shrink-0">
                    <svg
                      className="h-5 w-5 text-green-400"
                      viewBox="0 0 20 20"
                      fill="currentColor"
                    >
                      <path
                        fillRule="evenodd"
                        d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                        clipRule="evenodd"
                      />
                    </svg>
                  </div>
                  <div className="ml-3">
                    <h3 className="text-sm font-medium text-green-800">
                      Download Complete!
                    </h3>
                    <div className="mt-2 text-sm text-green-700">
                      <p>
                        Your files have been downloaded successfully. You can
                        find them in your Downloads folder.
                      </p>
                      <button
                        onClick={() => setShowDownloadNotification(false)}
                        className="mt-2 text-xs text-green-600 hover:text-green-500 underline"
                      >
                        Dismiss
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            )}

            <div className="mt-6 p-4 bg-gray-50 border border-gray-200 rounded-md">
              <h3 className="text-sm font-medium text-gray-900 mb-2">
                Terms of Use
              </h3>
              <p className="text-xs text-gray-700 leading-relaxed mb-3">
                By using this service, you confirm that you are the authorized 
                recipient of these files and agree to our Terms of Service. 
                This service is provided for secure, confidential document sharing.
              </p>
              <p className="text-xs text-gray-600">
                <a 
                  href="/terms" 
                  className="text-blue-600 hover:text-blue-500 underline"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  View full Terms of Service →
                </a>
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

// Loading component for Suspense fallback
function ShareFormLoading() {
  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center">
      <div className="text-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto mb-4"></div>
        <p className="text-gray-600">Loading...</p>
      </div>
    </div>
  );
}

export default function Home() {
  return (
    <Suspense fallback={<ShareFormLoading />}>
      <ShareForm />
    </Suspense>
  );
}
